import React, { useState } from 'react';

import '../styles/SaveStrategyModal.css';
import { HardpointTags, SNDTags, CTLTags } from '../data/tags.js';
import Select from 'react-select';
import { toast } from 'react-toastify';

const SaveStrategyModal = ({ map, mapName, mode, user, onCancel, onConfirm }) => {
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [title, setTitle] = useState(`Untitled_${Date.now().toString().slice(-4)}`);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [titleError, setTitleError] = useState('');

  // Select tags based on mode
 const modeMap = {
  HARDPOINT: HardpointTags,
  SEARCH_AND_DESTROY: SNDTags,
  SND: SNDTags,
  CONTROL: CTLTags,
  CTL: CTLTags
};

const normalizedMode = mode?.toUpperCase().replace(/\s+/g, '_');
const activeTags = modeMap[normalizedMode] || { zones: [], phases: [], styles: [] };



const customSelectStyles = {
  option: (provided, state) => ({
      ...provided,
  backgroundColor: '#282828',
  borderColor: state.isFocused || state.isHovered ? '#7E57C2' : '#1f2937',
  borderRadius: '8px',
  padding: '2px 4px',
  boxShadow: state.isFocused ? '0 0 0 2px rgba(126, 87, 194, 0.3)' : 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: '#7E57C2'
  }
}),
  multiValue: (provided) => ({
    ...provided,
    background: 'linear-gradient(to right, #a78bfa, #7E57C2)',
    borderRadius: '12px',
    padding: '2px 6px',
    color: '#fff'
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#fff',
    fontWeight: 600
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#fff',
    ':hover': {
      backgroundColor: '#ef4444',
      color: 'white'
    }
  }),
  control: (provided) => ({
    ...provided,
    backgroundColor: '#282828',
    borderColor: '#1f2937',
    borderRadius: '8px',
    padding: '2px 4px',
    boxShadow: 'none'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#282828',
    borderRadius: 8,
    padding: 8,
    zIndex: 10000
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10000
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af'
  })
};












  // Convert arrays to react-select format
  const toOptions = (arr) => arr.map(item => ({ label: item, value: item }));

  return (
    <div className="modal-overlay">
      <div className="save-modal">

        {/* Map Image Preview */}
        <div
          className="map-preview"
          style={{ backgroundImage: `url(${map?.thumbnail || map?.image})` }}
        />

        {/* Strategy Title Input */}
        <input
          className="strategy-title-input"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError('');
          }}
          placeholder="Enter Strategy Title"
          required
          aria-invalid={titleError ? 'true' : 'false'}
        />

        {/* Metadata Info */}
        <div className="strategy-meta">
          <div className="meta-row">
            <div className="meta-label">Created</div>
            <div className="meta-value">{new Date().toLocaleString()}</div>
          </div>
          <div className="meta-row">
            <div className="meta-label">By</div>
            <div className="meta-value"> {user?.ign || user?.email || 'Unknown'}
            </div>

          </div>
          <div className="meta-row">
            <div className="meta-label">Map</div>
            <div className="meta-value">{mapName}</div>
          </div>
          <div className="meta-row">
            <div className="meta-label">Mode</div>
            <div className="meta-value">{mode?.toUpperCase() || 'N/A'}</div>
          </div>
        </div>

       <div className="tag-selectors">
         <div className="tag-selector">
           <label>Zone Tags</label>
           <Select
             isMulti
             styles={customSelectStyles}
             options={toOptions(activeTags.zones)}
             onChange={(values) => setSelectedZones(values.map(v => v.value))}
             className="react-select"
             menuPortalTarget={document.body}
             menuPosition="fixed"
           />
         </div>

         <div className="tag-selector">
           <label>Phase Tags</label>
           <Select
             isMulti
             styles={customSelectStyles}
             options={toOptions(activeTags.phases)}
             onChange={(values) => setSelectedPhases(values.map(v => v.value))}
             className="react-select"
             menuPortalTarget={document.body}
             menuPosition="fixed"
           />
         </div>

         <div className="tag-selector">
           <label>Style Tags</label>
           <Select
             isMulti
             styles={customSelectStyles}
             options={toOptions(activeTags.styles)}
             onChange={(values) => setSelectedStyles(values.map(v => v.value))}
             className="react-select"
             menuPortalTarget={document.body}
             menuPosition="fixed"
           />
         </div>
       </div>

{(selectedZones.length > 0 || selectedPhases.length > 0 || selectedStyles.length > 0) && (
  <div className="tag-preview-section">
    <div className="preview-label">Selected Tags:</div>
    {[...selectedZones, ...selectedPhases, ...selectedStyles].map((tag, idx) => (
      <span key={idx} className="tag-chip">{tag}</span>
    ))}
  </div>
)}





       <div className="modal-actions">
  <button
    onClick={async () => {
      if (isSaving) return;
      const trimmedTitle = title.trim();
      if (!trimmedTitle) {
        setTitleError('Title is required');
        toast.error('Please enter a strategy title.');
        return;
      }

      setIsSaving(true);
      const success = await onConfirm({
        title: trimmedTitle,
        tags: {
          zones: selectedZones,
          phases: selectedPhases,
          styles: selectedStyles
        }
      });
      setIsSaving(false);

      if (success) {
        setSaveSuccess(true); // Show success message
        toast.success('Strategy saved to your playbook!');
      }
    }}
    className="confirm-button"
    disabled={isSaving || !title.trim()}
  >
    {isSaving ? 'Saving...' : 'Confirm Save'}
  </button>
  <button onClick={onCancel} className="cancel-button" disabled={isSaving}>Cancel</button>
</div>


{saveSuccess && (
  <div style={{
    color: '#4CAF50',
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: '1rem'
  }}>
    Strategy saved successfully!
  </div>
)}

      </div>
    </div>
  );
};

export default SaveStrategyModal;
